import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MultiSelect from "@/components/MultiSelect";
import CcInput from "@/components/CcInput";
import Modal from "@/components/Modal";
import SortableTh, { sortRows } from "@/components/SortableTh";
import { toast } from "sonner";
import { Download, FileText, Filter, Play, AlertTriangle, Bookmark, Save, Trash2, Send, Calendar } from "lucide-react";

const STATUS_OPTS = [
    { value: "new", label: "Nuevo" },
    { value: "open", label: "Abierto" },
    { value: "pending", label: "Pendiente" },
    { value: "resolved", label: "Resuelto" },
    { value: "closed", label: "Cerrado" },
];
const PRIORITY_OPTS = [
    { value: "low", label: "Baja" },
    { value: "medium", label: "Media" },
    { value: "high", label: "Alta" },
    { value: "urgent", label: "Urgente" },
];
const WAITING_OPTS = [
    { value: "agent", label: "Esperando al agente" },
    { value: "customer", label: "Esperando al cliente" },
    { value: "third_party", label: "Esperando a terceros" },
    { value: "none", label: "Sin estado de espera" },
];

const fmtDate = (iso) => {
    if (!iso) return "—";
    return iso.slice(0, 16).replace("T", " ");
};

export default function Reports() {
    const { user } = useAuth();
    const isStaff = ["agent", "admin", "superadmin", "super_agent"].includes(user?.role);

    const [searchParams] = useSearchParams();

    const [filters, setFilters] = useState({
        status: [], priority: [], waiting_state: [],
        company_id: [], assignee: [], requester: [], group_id: [],
        created_from: "", created_to: "",
        critical_dates_due_by: searchParams.get("critical_dates_due_by") || "",
        only_overdue_sla: false, q: "",
    });

    const [companies, setCompanies] = useState([]);
    const [groups, setGroups] = useState([]);
    const [allUsers, setAllUsers] = useState([]);

    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");
    const [exporting, setExporting] = useState({ csv: false, pdf: false });

    // Saved reports
    const [savedReports, setSavedReports] = useState([]);
    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [saveForm, setSaveForm] = useState({ name: "", frequency: "none", format: "pdf", day: 1, hour: 8, extra_recipients: [] });
    const [saving, setSaving] = useState(false);
    const [sendingId, setSendingId] = useState("");
    const [directory, setDirectory] = useState([]);

    // Sorting for main results + saved reports
    const [rowsSort, setRowsSort] = useState({ field: "ticket_id", dir: "desc" });
    const [savedSort, setSavedSort] = useState({ field: "name", dir: "asc" });

    const sortedRows = useMemo(() => sortRows(rows, rowsSort, {
        priority: (t) => ({ urgent: 4, high: 3, medium: 2, low: 1 })[t.priority] || 0,
    }), [rows, rowsSort]);
    const sortedSaved = useMemo(() => sortRows(savedReports, savedSort, {
        schedule: (r) => `${r?.schedule?.frequency || "z"}-${String(r?.schedule?.day ?? 99).padStart(2, "0")}-${String(r?.schedule?.hour ?? 99).padStart(2, "0")}`,
    }), [savedReports, savedSort]);

    const loadSavedReports = async () => {
        try {
            const r = await api.get("/saved-reports");
            setSavedReports(r.data || []);
        } catch { /* ignore */ }
    };

    const seedDefaultCriticalWeek = async () => {
        try {
            const { data } = await api.post("/saved-reports/seed-default-critical-week");
            if (data.created) {
                toast.success("Reporte creado: 'Fechas críticas de la semana'. Se enviará cada lunes 08:00.");
            } else {
                toast.info("Ya tenías ese reporte guardado. Sin cambios.");
            }
            await loadSavedReports();
        } catch (e) {
            toast.error(formatApiError(e, "No se pudo crear el reporte predeterminado"));
        }
    };

    useEffect(() => { loadSavedReports(); }, []);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/users/directory");
                setDirectory(r.data || []);
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const [c] = await Promise.all([api.get("/companies")]);
                setCompanies(c.data);
            } catch { /* ignore */ }
            if (isStaff) {
                try {
                    const [g, u] = await Promise.all([api.get("/groups"), api.get("/users")]);
                    setGroups(g.data || []);
                    setAllUsers(u.data || []);
                } catch { /* ignore */ }
            }
        })();
    }, [isStaff]);

    const agentOptions = useMemo(() => {
        const list = (allUsers || []).filter((u) => ["agent", "admin", "superadmin", "super_agent"].includes(u.role));
        return [
            { value: "me", label: "Asignados a mí" },
            { value: "unassigned", label: "Sin asignar" },
            ...list.map((u) => ({ value: u.user_id, label: u.name, sub: u.email })),
        ];
    }, [allUsers]);
    const requesterOptions = useMemo(() => {
        const list = (allUsers || []).filter((u) => ["customer", "super_customer"].includes(u.role));
        return list.map((u) => ({ value: u.user_id, label: u.name, sub: u.email }));
    }, [allUsers]);
    const companyOptions = useMemo(
        () => (companies || []).filter((c) => c.is_special !== "all").map((c) => ({ value: c.company_id, label: c.name })),
        [companies]
    );
    const groupOptions = useMemo(
        () => (groups || []).map((g) => ({ value: g.group_id, label: g.name })),
        [groups]
    );

    const buildPayload = (format = "json") => ({
        filters: buildCleanFilters(),
        format,
        limit: 1000,
    });

    const runReport = async () => {
        setLoading(true); setErr("");
        try {
            const resp = await api.post("/reports/tickets", buildPayload("json"));
            setRows(resp.data.rows || []);
            setSummary(resp.data.summary);
        } catch (e) {
            setErr(formatApiError(e));
            setRows([]); setSummary(null);
        } finally {
            setLoading(false);
        }
    };

    const exportFile = async (format) => {
        setExporting((s) => ({ ...s, [format]: true }));
        try {
            const resp = await api.post("/reports/tickets", buildPayload(format), { responseType: "blob" });
            const blob = new Blob([resp.data], {
                type: format === "csv" ? "text/csv;charset=utf-8" : "application/pdf",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "").slice(0, 12);
            a.href = url;
            a.download = `reporte_tickets_${stamp}.${format}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setExporting((s) => ({ ...s, [format]: false }));
        }
    };

    const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

    const buildCleanFilters = () => {
        const cleaned = {};
        for (const [k, v] of Object.entries(filters)) {
            if (v === "" || v === false || v == null) continue;
            if (Array.isArray(v) && v.length === 0) continue;
            cleaned[k] = v;
        }
        if (cleaned.created_from) cleaned.created_from = `${cleaned.created_from.slice(0, 10)}T00:00:00`;
        if (cleaned.created_to) cleaned.created_to = `${cleaned.created_to.slice(0, 10)}T23:59:59`;
        return cleaned;
    };

    const saveCurrentReport = async (e) => {
        e?.preventDefault();
        if (!saveForm.name.trim()) {
            toast.error("Indica un nombre para el reporte");
            return;
        }
        setSaving(true);
        try {
            await api.post("/saved-reports", {
                name: saveForm.name.trim(),
                filters: buildCleanFilters(),
                schedule: {
                    frequency: saveForm.frequency,
                    format: saveForm.format,
                    day: saveForm.day,
                    hour: saveForm.hour,
                },
                extra_recipients: saveForm.extra_recipients || [],
            });
            toast.success(`Reporte "${saveForm.name.trim()}" guardado`);
            setSaveModalOpen(false);
            setSaveForm({ name: "", frequency: "none", format: "pdf", day: 1, hour: 8, extra_recipients: [] });
            await loadSavedReports();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setSaving(false);
        }
    };

    const loadSavedFilters = (sr) => {
        const f = sr.filters || {};
        const toArr = (v) => Array.isArray(v) ? v : (v ? [v] : []);
        setFilters({
            status: toArr(f.status), priority: toArr(f.priority), waiting_state: toArr(f.waiting_state),
            company_id: toArr(f.company_id), assignee: toArr(f.assignee), requester: toArr(f.requester),
            group_id: toArr(f.group_id),
            created_from: (f.created_from || "").slice(0, 10),
            created_to: (f.created_to || "").slice(0, 10),
            only_overdue_sla: !!f.only_overdue_sla,
            q: f.q || "",
        });
        toast.success(`Filtros de "${sr.name}" cargados`);
    };

    const sendSavedNow = async (sr) => {
        setSendingId(sr.report_id);
        try {
            const r = await api.post(`/saved-reports/${sr.report_id}/send`);
            toast.success(`Enviado a ${r.data.sent_to} (${r.data.rows} tickets)`);
            await loadSavedReports();
        } catch (e2) {
            toast.error(formatApiError(e2));
        } finally {
            setSendingId("");
        }
    };

    const deleteSaved = async (sr) => {
        if (!window.confirm(`¿Eliminar el reporte guardado "${sr.name}"?`)) return;
        try {
            await api.delete(`/saved-reports/${sr.report_id}`);
            toast.success("Reporte eliminado");
            await loadSavedReports();
        } catch (e2) {
            toast.error(formatApiError(e2));
        }
    };

    const formatSchedule = (sr) => {
        const s = sr.schedule || {};
        if (!s.frequency || s.frequency === "none") return "Sin envío automático";
        const fmt = s.format === "both" ? "CSV + PDF" : (s.format || "pdf").toUpperCase();
        if (s.frequency === "weekly") {
            const dayName = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"][(s.day || 1) - 1] || "Lunes";
            return `Semanal · ${dayName} ${String(s.hour || 8).padStart(2, "0")}:00 · ${fmt}`;
        }
        if (s.frequency === "monthly") {
            return `Mensual · día ${s.day || 1} · ${String(s.hour || 8).padStart(2, "0")}:00 · ${fmt}`;
        }
        return s.frequency;
    };

    return (
        <div className="container" data-testid="reports-page">
            <div className="page-header">
                <h1 className="page-title">Reportes</h1>
                <p className="page-sub text-muted">Genera reportes de tickets bajo demanda con los criterios que necesites. Respeta tu nivel de acceso.</p>
            </div>

            <div className="card mt-4" data-testid="reports-filters">
                <div className="card-header">
                    <h3><Filter size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />Filtros</h3>
                </div>
                <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
                    <div className="field">
                        <label>Status</label>
                        <MultiSelect
                            options={STATUS_OPTS}
                            value={filters.status}
                            onChange={(v) => updateFilter("status", v)}
                            placeholder="Todos"
                            testid="filter-status"
                        />
                    </div>
                    <div className="field">
                        <label>Prioridad</label>
                        <MultiSelect
                            options={PRIORITY_OPTS}
                            value={filters.priority}
                            onChange={(v) => updateFilter("priority", v)}
                            placeholder="Todas"
                            testid="filter-priority"
                        />
                    </div>
                    <div className="field">
                        <label>Estado de espera</label>
                        <MultiSelect
                            options={WAITING_OPTS}
                            value={filters.waiting_state}
                            onChange={(v) => updateFilter("waiting_state", v)}
                            placeholder="Cualquiera"
                            testid="filter-waiting"
                        />
                    </div>
                    <div className="field">
                        <label>Empresa</label>
                        <MultiSelect
                            options={companyOptions}
                            value={filters.company_id}
                            onChange={(v) => updateFilter("company_id", v)}
                            placeholder="Todas"
                            testid="filter-company"
                        />
                    </div>
                    {isStaff && (
                        <>
                            <div className="field">
                                <label>Agente asignado</label>
                                <MultiSelect
                                    options={agentOptions}
                                    value={filters.assignee}
                                    onChange={(v) => updateFilter("assignee", v)}
                                    placeholder="Todos"
                                    testid="filter-assignee"
                                />
                            </div>
                            <div className="field">
                                <label>Solicitante</label>
                                <MultiSelect
                                    options={requesterOptions}
                                    value={filters.requester}
                                    onChange={(v) => updateFilter("requester", v)}
                                    placeholder="Cualquiera"
                                    testid="filter-requester"
                                />
                            </div>
                            <div className="field">
                                <label>Grupo</label>
                                <MultiSelect
                                    options={groupOptions}
                                    value={filters.group_id}
                                    onChange={(v) => updateFilter("group_id", v)}
                                    placeholder="Todos"
                                    testid="filter-group"
                                />
                            </div>
                        </>
                    )}
                    <div className="field">
                        <label>Creado desde</label>
                        <input type="date" className="input" value={filters.created_from} onChange={(e) => updateFilter("created_from", e.target.value)} data-testid="filter-created-from" />
                    </div>
                    <div className="field">
                        <label>Creado hasta</label>
                        <input type="date" className="input" value={filters.created_to} onChange={(e) => updateFilter("created_to", e.target.value)} data-testid="filter-created-to" />
                    </div>
                    <div className="field">
                        <label>Texto libre</label>
                        <input type="text" className="input" placeholder="Asunto, descripción, ID, etiqueta…" value={filters.q} onChange={(e) => updateFilter("q", e.target.value)} data-testid="filter-q" />
                    </div>
                    <div className="field" data-testid="filter-critical-dates-due-by-wrap">
                        <label title="Filtra tickets con al menos una fecha crítica NO marcada como cumplida cuya fecha sea anterior o igual a la elegida.">
                            📅 Con fechas críticas vencidas o por vencer a la fecha
                        </label>
                        <input
                            type="date"
                            className="input"
                            value={filters.critical_dates_due_by}
                            onChange={(e) => updateFilter("critical_dates_due_by", e.target.value)}
                            data-testid="filter-critical-dates-due-by"
                        />
                        {filters.critical_dates_due_by && (
                            <button
                                type="button"
                                className="linklike"
                                onClick={() => updateFilter("critical_dates_due_by", "")}
                                data-testid="filter-critical-dates-due-by-clear"
                                style={{ marginTop: 4, fontSize: 11, color: "var(--text-muted)" }}
                            >
                                Limpiar fecha
                            </button>
                        )}
                    </div>
                    <div className="field" style={{ alignSelf: "end" }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input type="checkbox" checked={filters.only_overdue_sla} onChange={(e) => updateFilter("only_overdue_sla", e.target.checked)} data-testid="filter-overdue" />
                            <span><AlertTriangle size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} /> Solo con SLA vencido</span>
                        </label>
                    </div>
                </div>
                <div style={{ padding: "12px 16px 16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div className="flex gap-2 flex-wrap">
                        <button className="btn btn-primary" onClick={runReport} disabled={loading} data-testid="reports-run">
                            {loading ? <span className="loader" /> : <><Play size={13} /> Generar reporte</>}
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setSaveModalOpen(true)} data-testid="reports-save-btn" title="Guardar estos filtros como un reporte reutilizable y opcionalmente programado">
                            <Save size={13} /> Guardar / Programar
                        </button>
                    </div>
                    {summary && (
                        <div className="flex gap-2">
                            <button className="btn btn-ghost btn-sm" onClick={() => exportFile("csv")} disabled={exporting.csv} data-testid="reports-export-csv">
                                {exporting.csv ? <span className="loader" /> : <><Download size={13} /> Exportar CSV</>}
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => exportFile("pdf")} disabled={exporting.pdf} data-testid="reports-export-pdf">
                                {exporting.pdf ? <span className="loader" /> : <><FileText size={13} /> Exportar PDF</>}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {savedReports.length > 0 ? (
                <div className="card mt-4" data-testid="reports-saved-list">
                    <div className="card-header">
                        <h3><Bookmark size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />Reportes guardados</h3>
                        <div className="tbar" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={seedDefaultCriticalWeek}
                                data-testid="reports-seed-critical-week"
                                title="Crea (si no existe) el reporte semanal de fechas críticas: cada lunes 08:00 con todo lo que vence en los próximos 7 días."
                            >
                                🌱 + Reporte semanal de fechas críticas
                            </button>
                            <span className="text-muted text-sm">{savedReports.length}</span>
                            <select className="select" value={`${savedSort.field}:${savedSort.dir}`} onChange={(e) => { const [f, d] = e.target.value.split(":"); setSavedSort({ field: f, dir: d }); }} data-testid="saved-reports-sort">
                                <option value="name:asc">Nombre A-Z</option>
                                <option value="name:desc">Nombre Z-A</option>
                                <option value="schedule:asc">Programación asc</option>
                                <option value="schedule:desc">Programación desc</option>
                                <option value="last_run_at:desc">Último envío (reciente)</option>
                                <option value="last_run_at:asc">Último envío (antiguo)</option>
                            </select>
                        </div>
                    </div>
                    <div style={{ padding: 6 }}>
                        {sortedSaved.map((sr) => (
                            <div key={sr.report_id} data-testid={`saved-report-${sr.report_id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "10px 12px", borderTop: "1px solid var(--border)" }}>
                                <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{ fontWeight: 600, fontSize: 13.5 }}>{sr.name}</div>
                                    <div className="text-muted" style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                                        <Calendar size={11} /> {formatSchedule(sr)}
                                        {sr.last_run_at && <span> · Último envío {sr.last_run_at.slice(0, 16).replace("T", " ")}</span>}
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button className="btn btn-ghost btn-sm" onClick={() => loadSavedFilters(sr)} data-testid={`saved-load-${sr.report_id}`} title="Cargar estos filtros">
                                        <Filter size={12} /> Cargar
                                    </button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => sendSavedNow(sr)} disabled={sendingId === sr.report_id} data-testid={`saved-send-${sr.report_id}`} title="Generar y enviarme ahora por email">
                                        {sendingId === sr.report_id ? <span className="loader" /> : <><Send size={12} /> Enviar ahora</>}
                                    </button>
                                    <button className="btn btn-ghost btn-sm" onClick={() => deleteSaved(sr)} data-testid={`saved-delete-${sr.report_id}`} title="Eliminar reporte guardado">
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <div className="card mt-4" data-testid="reports-saved-empty" style={{ padding: 16, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 14 }}><Bookmark size={13} style={{ display: "inline", marginRight: 6, verticalAlign: "middle" }} />Reportes guardados</h3>
                        <p className="text-muted" style={{ margin: "4px 0 0", fontSize: 12 }}>
                            Aún no tienes reportes guardados. Empezá con el "panel de batalla del lunes":
                        </p>
                    </div>
                    <button
                        className="btn btn-primary btn-sm"
                        onClick={seedDefaultCriticalWeek}
                        data-testid="reports-seed-critical-week-empty"
                        title="Crea el reporte semanal de fechas críticas que se envía cada lunes 08:00 con todo lo que vence en los próximos 7 días."
                    >
                        🌱 Crear reporte semanal de fechas críticas
                    </button>
                </div>
            )}

            {err && <div className="alert alert-error mt-4">{err}</div>}

            {summary && (
                <div className="card mt-4" data-testid="reports-summary">
                    <div className="card-header">
                        <h3>Resumen</h3>
                        <span className="text-muted text-sm">Generado {fmtDate(summary.generated_at)}</span>
                    </div>
                    <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
                        <KpiCell label="Total" value={summary.count} testid="kpi-total" />
                        <KpiCell label="Abiertos" value={summary.open} accent="var(--accent)" testid="kpi-open" />
                        <KpiCell label="Resueltos" value={summary.resolved} testid="kpi-resolved" />
                        <KpiCell label="SLA vencidos" value={summary.overdue} accent={summary.overdue > 0 ? "#DC2626" : undefined} testid="kpi-overdue" />
                    </div>
                </div>
            )}

            {summary && (
                <div className="card mt-4">
                    <div className="card-header">
                        <h3>Detalle</h3>
                        <span className="text-muted text-sm">{rows.length} ticket{rows.length === 1 ? "" : "s"}</span>
                    </div>
                    <div className="table-wrap" style={{ overflowX: "auto" }}>
                        <table className="t" data-testid="reports-table">
                            <thead>
                                <tr>
                                    <SortableTh field="ticket_id" sort={rowsSort} onChange={setRowsSort}>Ticket</SortableTh>
                                    <SortableTh field="subject" sort={rowsSort} onChange={setRowsSort}>Asunto</SortableTh>
                                    <SortableTh field="status" sort={rowsSort} onChange={setRowsSort}>Status</SortableTh>
                                    <SortableTh field="priority" sort={rowsSort} onChange={setRowsSort}>Prio.</SortableTh>
                                    <SortableTh field="company_name" sort={rowsSort} onChange={setRowsSort}>Empresa</SortableTh>
                                    <SortableTh field="requester_name" sort={rowsSort} onChange={setRowsSort}>Solicitante</SortableTh>
                                    <SortableTh field="assignee_name" sort={rowsSort} onChange={setRowsSort}>Agente</SortableTh>
                                    <SortableTh field="last_customer_action_at" sort={rowsSort} onChange={setRowsSort}>Últ. cliente</SortableTh>
                                    <SortableTh field="last_agent_action_at" sort={rowsSort} onChange={setRowsSort}>Últ. agente</SortableTh>
                                    <SortableTh field="is_overdue" sort={rowsSort} onChange={setRowsSort}>SLA</SortableTh>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.length === 0 && (
                                    <tr><td colSpan={10} className="text-muted text-sm" style={{ textAlign: "center", padding: 24 }}>Sin resultados con los criterios seleccionados.</td></tr>
                                )}
                                {sortedRows.map((r) => (
                                    <tr key={r.ticket_id} data-testid={`report-row-${r.ticket_id}`}>
                                        <td><Link to={`/tickets/${r.ticket_id}`} className="linklike mono">{r.ticket_id}</Link></td>
                                        <td style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.subject}><b>{r.subject}</b></td>
                                        <td><span className={`badge status-${r.status}`}>{r.status}</span></td>
                                        <td><span className={`badge priority-${r.priority}`}>{r.priority}</span></td>
                                        <td className="text-muted">{r.company_name || "—"}</td>
                                        <td>
                                            {r.requester_name || "—"}
                                            {r.requester_email && <div className="text-muted mono text-xs">{r.requester_email}</div>}
                                        </td>
                                        <td>{r.assignee_name || <span className="text-muted">Sin asignar</span>}</td>
                                        <td className="mono text-xs">{fmtDate(r.last_customer_action_at)}</td>
                                        <td className="mono text-xs">{fmtDate(r.last_agent_action_at)}</td>
                                        <td>
                                            {r.is_overdue
                                                ? <span className="badge" style={{ background: "#991B1B", color: "white" }}>VENCIDO</span>
                                                : <span className="badge" style={{ background: "#0D5024", color: "white" }}>OK</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {saveModalOpen && (
                <Modal open={true} title="Guardar reporte" onClose={() => setSaveModalOpen(false)} testId="save-report-modal-wrap">
                    <form onSubmit={saveCurrentReport} className="form-grid" data-testid="save-report-modal">
                        <div className="field">
                            <label>Nombre del reporte</label>
                            <input
                                type="text"
                                className="input"
                                value={saveForm.name}
                                onChange={(e) => setSaveForm({ ...saveForm, name: e.target.value })}
                                placeholder="Ej. Backlog crítico, Tickets ACME últimos 30 días…"
                                maxLength={80}
                                autoFocus
                                data-testid="save-report-name"
                            />
                            <p className="text-muted text-xs" style={{ marginTop: 4 }}>Los filtros activos se guardarán junto con este nombre.</p>
                        </div>
                        <div className="field">
                            <label>Frecuencia de envío automático</label>
                            <select className="select" value={saveForm.frequency} onChange={(e) => setSaveForm({ ...saveForm, frequency: e.target.value })} data-testid="save-report-frequency">
                                <option value="none">No enviar automáticamente (manual)</option>
                                <option value="weekly">Semanal</option>
                                <option value="monthly">Mensual</option>
                            </select>
                        </div>
                        {saveForm.frequency !== "none" && (
                            <>
                                <div className="field">
                                    <label>Formato del archivo</label>
                                    <select className="select" value={saveForm.format} onChange={(e) => setSaveForm({ ...saveForm, format: e.target.value })} data-testid="save-report-format">
                                        <option value="pdf">PDF</option>
                                        <option value="csv">CSV (Excel)</option>
                                        <option value="both">Ambos (PDF + CSV)</option>
                                    </select>
                                </div>
                                <div className="grid-2">
                                    <div className="field">
                                        <label>{saveForm.frequency === "weekly" ? "Día de la semana" : "Día del mes"}</label>
                                        {saveForm.frequency === "weekly" ? (
                                            <select className="select" value={saveForm.day} onChange={(e) => setSaveForm({ ...saveForm, day: parseInt(e.target.value, 10) })} data-testid="save-report-day">
                                                <option value={1}>Lunes</option>
                                                <option value={2}>Martes</option>
                                                <option value={3}>Miércoles</option>
                                                <option value={4}>Jueves</option>
                                                <option value={5}>Viernes</option>
                                                <option value={6}>Sábado</option>
                                                <option value={7}>Domingo</option>
                                            </select>
                                        ) : (
                                            <input type="number" min={1} max={28} className="input" value={saveForm.day} onChange={(e) => setSaveForm({ ...saveForm, day: parseInt(e.target.value, 10) || 1 })} data-testid="save-report-day" />
                                        )}
                                    </div>
                                    <div className="field">
                                        <label>Hora (CDMX)</label>
                                        <input type="number" min={0} max={23} className="input" value={saveForm.hour} onChange={(e) => setSaveForm({ ...saveForm, hour: parseInt(e.target.value, 10) || 0 })} data-testid="save-report-hour" />
                                    </div>
                                </div>
                                <div className="field">
                                    <label>Destinatarios adicionales (Cc)</label>
                                    <CcInput
                                        value={saveForm.extra_recipients}
                                        onChange={(arr) => setSaveForm({ ...saveForm, extra_recipients: arr })}
                                        suggestions={directory}
                                        placeholder="Empieza a escribir un nombre o correo…"
                                        testid="save-report-extra-recipients"
                                    />
                                    <p className="text-muted text-xs" style={{ marginTop: 4 }}>Selecciona del directorio o teclea correos libres. Recibirán una copia del reporte programado además de ti.</p>
                                </div>
                            </>
                        )}
                        <div className="modal-actions">
                            <button type="button" className="btn btn-ghost" onClick={() => setSaveModalOpen(false)}>Cancelar</button>
                            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-report-submit">
                                {saving ? <span className="loader" /> : <><Save size={13} /> Guardar</>}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
}

function KpiCell({ label, value, accent, testid }) {
    return (
        <div style={{ padding: 14, background: "var(--bg-2)", borderRadius: 10, border: "1px solid var(--border-strong)" }} data-testid={testid}>
            <div className="text-muted" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: accent || "var(--text)" }}>{value}</div>
        </div>
    );
}
